import { createMyGivingPinGuard } from './guards/mygiving-pin-guard';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import {
  BUILDING_FUND_PLEDGE_FLAG,
  BUILDING_FUND_PLEDGE_PATH,
  BF_PLEDGE_ROUTES,
} from '@/constants/MyGiving/building-fund-pledge';
import { isBfPledgeOpen } from '@/utils/building-fund-pledge';

import BuildingFund from '../pages/my-giving/building-fund/_id.vue';
import BuildingFundPledgeForm from '../pages/my-giving/building-fund-pledge/form.vue';
import BuildingFundPledgeConfirm from '../pages/my-giving/building-fund-pledge/confirm.vue';
import BuildingFundPledgeDone from '../pages/my-giving/building-fund-pledge/done.vue';
import BuildingFundPledgeView from '../pages/my-giving/building-fund-pledge/view.vue';
import ContactUs from '../pages/my-giving/contact-us.vue';
import LocalTransfer from '../pages/my-giving/egiro/local-transfer.vue';
import OverseasTransfer from '../pages/my-giving/egiro/overseas-transfer.vue';
import PayNow from '../pages/my-giving/egiro/paynow.vue';
import FAQ from '../pages/my-giving/faq.vue';
import GivingMode from '../pages/my-giving/giving-mode.vue';
import GivingOptions from '../pages/my-giving/giving-options.vue';
import MyGiving from '../pages/my-giving/index.vue';
import EgiroBankConfirmation from '../pages/my-giving/link-bank/_bankCode.vue';
import ListOfBanks from '../pages/my-giving/list-of-banks.vue';
import BoRedirection from '../pages/my-giving/bo-redirection.vue';
import TitheHistory from '../pages/my-giving/tithe-history/_id.vue';
import ManageGivingMethods from '../pages/my-giving/manage.vue';
import TithingMessage from '../pages/my-giving/tithe-msg/tithing-message.vue';
import TitheFirstTime from '../pages/my-giving/tithe-msg/tithe-first-time.vue';
import LocalTransferEgiro from '../pages/my-giving/egiro-transaction/local-transfer-egiro/index.vue';
import TransactionStatus from '../pages/my-giving/egiro-transaction/local-transfer-egiro/status/_transactionId.vue';
import RemoveBank from '../pages/my-giving/remove-bank.vue';
import RemoveRecurring from '../pages/my-giving/remove-recurring.vue';
import ApplicationInProgress from '../pages/my-giving/application-in-progress.vue';
import SetupRecurringStart from '../pages/my-giving/setup-recurring-start.vue';
import CreatePinPage from '../pages/my-giving/Pin/CreatePinPage.vue';
import ForgotPinPage from '../pages/my-giving/Pin/ForgotPinPage.vue';
import ForgotPinOtpPage from '../pages/my-giving/Pin/ForgotPinOtpPage.vue';
import ForgotPinSuccessPage from '../pages/my-giving/Pin/ForgotPinSuccessPage.vue';
import WaitingForLink from '../pages/my-giving/waiting-for-link.vue';
import { MY_GIVING_WAITING_FOR_LINK_PATH } from '@/constants/MyGiving/waiting-for-link';

const MY_GIVING_META = { disableSwipeNavigation: true };

export function createMyGivingRoutes(store) {
  const pinGuard = createMyGivingPinGuard(store);

  // PIN first, then the feature flag — composed by hand, as every other guard in
  // the router is (see the note on `beforeEnter: [a, b]` in router/index.js).
  // The flag is read from the cache the landing page already used to decide
  // whether to show the banner, so a member only reaches here with it on; a
  // deep link with it off lands back on /my-giving.
  const pledgeGuard = (to, from, next) =>
    pinGuard(to, from, redirect => {
      if (redirect !== undefined) return next(redirect);
      const { isEnabled } = useFeatureFlags();
      if (!isEnabled(BUILDING_FUND_PLEDGE_FLAG)) return next('/my-giving');
      return next();
    });

  // A pledge page that renders something only the store holds: a deep link or a
  // reload has nothing to show, so it goes to `fallback` instead.
  const pledgeStateGuard = (key, fallback) => (to, from, next) =>
    pledgeGuard(to, from, redirect => {
      if (redirect !== undefined) return next(redirect);
      if (!store.state.buildingFundPledge?.[key]) return next(fallback);
      return next();
    });
  // The confirm page shows and sends the pledge the form left; the done page shows
  // what was just submitted. With the window closed (BF_PLEDGE_OPEN_FLAG off) there
  // is nothing to send, so Confirm goes back to the form, where Continue is disabled.
  const pendingGuard = pledgeStateGuard('pending', BF_PLEDGE_ROUTES.form);
  const confirmGuard = (to, from, next) =>
    isBfPledgeOpen() ? pendingGuard(to, from, next) : next(BF_PLEDGE_ROUTES.form);
  const doneGuard = pledgeStateGuard('submitted', BF_PLEDGE_ROUTES.view);

  return [
    // Landing page — has the PIN overlay, no guard needed
    {
      path: '/my-giving',
      component: MyGiving,
      meta: MY_GIVING_META,
    },

    // A login still waiting for its CG leader's link has no MyGiving yet; the
    // global MyGiving link guard (guards/mygiving-link-guard.js) sends it here.
    {
      path: MY_GIVING_WAITING_FOR_LINK_PATH,
      component: WaitingForLink,
      meta: MY_GIVING_META,
    },

    // PIN management — no guard (user may not have verified yet)
    {
      path: '/my-giving/pin/create-pin',
      component: CreatePinPage,
      meta: MY_GIVING_META,
    },
    {
      path: '/my-giving/pin/forgot-pin',
      component: ForgotPinPage,
      meta: MY_GIVING_META,
    },
    {
      path: '/my-giving/pin/forgot-pin/otp',
      component: ForgotPinOtpPage,
      meta: MY_GIVING_META,
    },
    {
      path: '/my-giving/pin/forgot-pin/success',
      component: ForgotPinSuccessPage,
      meta: MY_GIVING_META,
    },

    // Bank OAuth callback — no guard (bank redirects here externally)
    {
      path: '/my-giving/bo-redirection',
      component: BoRedirection,
      meta: MY_GIVING_META,
    },

    // All sub-pages below require PIN verification via guard
    {
      path: '/my-giving/building-fund/:id',
      component: BuildingFund,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    // There is no first page any more; the form is the pledge card. Older links to
    // the bare path land on it, through its own guard.
    {
      path: BUILDING_FUND_PLEDGE_PATH,
      redirect: BF_PLEDGE_ROUTES.form,
      meta: MY_GIVING_META,
    },
    {
      path: BF_PLEDGE_ROUTES.form,
      component: BuildingFundPledgeForm,
      meta: MY_GIVING_META,
      beforeEnter: pledgeGuard,
    },
    {
      path: BF_PLEDGE_ROUTES.confirm,
      component: BuildingFundPledgeConfirm,
      meta: MY_GIVING_META,
      beforeEnter: confirmGuard,
    },
    {
      path: BF_PLEDGE_ROUTES.done,
      component: BuildingFundPledgeDone,
      meta: MY_GIVING_META,
      beforeEnter: doneGuard,
    },
    {
      path: BF_PLEDGE_ROUTES.view,
      component: BuildingFundPledgeView,
      meta: MY_GIVING_META,
      beforeEnter: pledgeGuard,
    },
    {
      path: '/my-giving/tithe-history/:id',
      component: TitheHistory,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/giving-mode',
      component: GivingMode,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/faq',
      component: FAQ,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/contact-us',
      component: ContactUs,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/local-transfer',
      component: LocalTransfer,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/overseas-transfer',
      component: OverseasTransfer,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/paynow',
      component: PayNow,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/giving-options',
      component: GivingOptions,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/list-of-banks',
      component: ListOfBanks,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/link-bank/:bankCode',
      component: EgiroBankConfirmation,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/tithing-message',
      component: TithingMessage,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/tithe-first-time',
      component: TitheFirstTime,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/manage',
      component: ManageGivingMethods,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/local-transfer-egiro',
      component: LocalTransferEgiro,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/local-transfer-egiro/status/:transactionId',
      component: TransactionStatus,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/remove-bank',
      component: RemoveBank,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/remove-recurring',
      component: RemoveRecurring,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/application-in-progress',
      component: ApplicationInProgress,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
    {
      path: '/my-giving/setup-recurring-start',
      component: SetupRecurringStart,
      meta: MY_GIVING_META,
      beforeEnter: pinGuard,
    },
  ];
}
